import type { FlexProps } from 'antd';
import {
  FC,
  HTMLAttributes,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { TableRow } from '../common';
import { useTableStoreValue } from '../TableContext';
import { WithFilterOptions, WithFilterOptionsProps } from './WithFilterOptions';
import { WithStickyHeaderIfApplicable } from './WithStickyHeaderIfApplicable';
import { TableShell } from './TableShell';
import { WithActions } from './WithActions';
import { WithRowDragTargetCrosshair } from '../../form/table/tanstack/WithRowDragTargetCrosshair';
import { Table as TableInstance, Row } from '@tanstack/react-table';
import { NoDataRow } from '../common/NoDataRow';
import { TableSpinner } from '../common/TableSpinner';

export interface VirtualRendererProps extends WithFilterOptionsProps {
  scrollContainer?: HTMLElement;
  actions?: ReactNode | ((table: TableInstance<any>) => ReactNode);
  actionsInFront?: boolean;
  actionsWrapperProps?: FlexProps;
  hideClearFilters?: boolean;
  hideEmptyImage?: boolean;
  rowHtmlAttributes?: (
    row: any,
    rowIndex: number,
  ) => HTMLAttributes<HTMLTableRowElement> | undefined;
  isLoading?: boolean;
  estimateSize?: (index: number) => number;
  className?: string;
}

export const VirtualRenderer: FC<VirtualRendererProps> = (props) => {
  const {
    scrollContainer,
    actions,
    actionsInFront,
    actionsWrapperProps,
    hideClearFilters,
    hideEmptyImage,
    rowHtmlAttributes,
    isLoading,
    estimateSize,
    className,
  } = props;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const scrollPaddingStart = scrollContainer
    ? (container?.getBoundingClientRect().top ?? 0) -
      scrollContainer.getBoundingClientRect().top +
      scrollContainer.scrollTop
    : 0;

  if (!scrollContainer) {
    return null;
  }

  return (
    <WithActions
      actions={actions}
      actionsInFront={actionsInFront}
      actionsWrapperProps={actionsWrapperProps}
      scrollContainer={scrollContainer}
      hideClearFilters={hideClearFilters}
    >
      {isLoading && <TableSpinner />}
      <WithStickyHeaderIfApplicable>
        <WithFilterOptions {...props}>
          <WithRowDragTargetCrosshair containerRef={setContainer}>
            <VirtualRendererInner
              scrollContainer={scrollContainer}
              scrollPaddingStart={scrollPaddingStart}
              rowHtmlAttributes={rowHtmlAttributes}
              isLoading={isLoading}
              estimateSize={estimateSize}
              className={className}
              hideEmptyImage={hideEmptyImage}
            />
          </WithRowDragTargetCrosshair>
        </WithFilterOptions>
      </WithStickyHeaderIfApplicable>
    </WithActions>
  );
};

export const DEFAULT_ROW_ESTIMATED_SIZE = 31;
const defaultEstimateSize = () => DEFAULT_ROW_ESTIMATED_SIZE;

const VirtualRendererInner: FC<{
  scrollContainer: HTMLElement;
  scrollPaddingStart?: number;
  rowHtmlAttributes?: (
    row: Row<any>,
    rowIndex: number,
  ) => HTMLAttributes<HTMLTableRowElement> | undefined;
  isLoading?: boolean;
  estimateSize?: (index: number) => number;
  className?: string;
  hideEmptyImage?: boolean;
}> = memo((props) => {
  const {
    scrollContainer,
    scrollPaddingStart,
    rowHtmlAttributes,
    isLoading,
    estimateSize = defaultEstimateSize,
    className,
    hideEmptyImage,
  } = props;
  const [table] = useTableStoreValue('table');
  const [, setVirtualizer] = useTableStoreValue('virtualizer');

  const { rows } = table.getRowModel();
  const colSpan = table.getAllColumns().length;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: useCallback(() => scrollContainer, [scrollContainer]),
    estimateSize,
    overscan: 15,
    scrollPaddingStart: scrollPaddingStart,
    scrollMargin: scrollPaddingStart,
  });

  useEffect(() => {
    setVirtualizer(virtualizer);
  }, [setVirtualizer, virtualizer]);

  const { getTotalSize, getVirtualItems, getOffsetForIndex } = virtualizer;

  const virtualItems = getVirtualItems();
  const firstIndex: number | undefined = virtualItems[0]?.index;
  const offsetTop = useMemo(
    () =>
      (typeof firstIndex === 'number' && getOffsetForIndex(firstIndex)?.[0]) ||
      0,
    [firstIndex, getOffsetForIndex],
  );

  return (
    <div
      style={{ height: virtualItems.length === 0 ? undefined : getTotalSize() }}
    >
      <TableShell className={className}>
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];
          return (
            <TableRow
              index={virtualRow.index}
              key={row.id}
              row={row}
              style={{
                transform: `translateY(${offsetTop}px)`,
              }}
              htmlAttributes={rowHtmlAttributes?.(row.original, row.index)}
            />
          );
        })}
        {virtualItems.length === 0 && !isLoading && (
          <NoDataRow colSpan={colSpan} hideImage={hideEmptyImage} />
        )}
      </TableShell>
    </div>
  );
});
